import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useEffect, useRef, useState, type VideoHTMLAttributes} from 'react'
import {
  ArrowLeftIcon,
  CopilotIcon,
  PeopleIcon,
  BookIcon,
  DeviceMobileIcon,
  IterationsIcon,
  ColumnsIcon,
  PlayIcon,
} from '@primer/octicons-react'
import {
  ThemeProvider,
  Hero,
  Grid,
  Image,
  LogoSuite,
  AnchorNav,
  SectionIntro,
  Bento,
  Link,
  Box,
  Text,
  River,
  Heading,
  Label,
  RiverBreakout,
  Timeline,
  Button,
  UnorderedList,
  Stack,
  Card,
  AnimationProvider,
  OrderedList,
} from '@primer/react-brand'
import type {FeatureCopilotPage} from '../../lib/types/contentful'
import {ContentfulFaqGroup} from '../../components/contentful/ContentfulFaqGroup/ContentfulFaqGroup'

interface Payload {
  copilotSignupPath: string
  copilotForBusinessSignupPath: string
  copilotContactSalesPath: string
  copilotHeroSignupPath: string
  isCopilotPlansPageEnabled: boolean
  copilotHydroTracking: string
  contentfulRawJsonResponse: unknown
  page: FeatureCopilotPage
}

export function ContentfulIndex(payload: Payload) {
  const {
    copilotSignupPath,
    copilotForBusinessSignupPath,
    copilotContactSalesPath,
    copilotHeroSignupPath,
    isCopilotPlansPageEnabled,
    copilotHydroTracking,
    page,
  } = payload

  // Hero Video player
  //
  // Video player with custom controls for the Hero

  const {heroVideoLg} = useRoutePayload<{heroVideoLg: string}>()
  const {heroVideoLgPoster} = useRoutePayload<{heroVideoLgPoster: string}>()
  const {heroVideoSm} = useRoutePayload<{heroVideoSm: string}>()

  const videoLgRef = useRef<HTMLVideoElement>(null)
  const videoSmRef = useRef<HTMLVideoElement>(null)
  const [videoState, setVideoState] = useState('playing')
  const [videoButtonLabel, setVideoButtonLabel] = useState('Pause')
  const [VideoIcon, setVideoIcon] = useState(() => ColumnsIcon)

  const handleVideoStateChange = () => {
    if (videoState === 'playing') {
      setVideoState('paused')
      setVideoButtonLabel('Play')
      setVideoIcon(() => PlayIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.pause()
        videoSmRef.current.pause()
      }
    } else if (videoState === 'paused') {
      setVideoState('playing')
      setVideoButtonLabel('Pause')
      setVideoIcon(() => ColumnsIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.play()
        videoSmRef.current.play()
      }
    } else if (videoState === 'ended') {
      setVideoState('playing')
      setVideoButtonLabel('Pause')
      setVideoIcon(() => ColumnsIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.currentTime = 0
        videoSmRef.current.currentTime = 0
        videoLgRef.current.play()
        videoSmRef.current.play()
      }
    }
  }

  const handleVideoStateEnd = () => {
    setVideoState('ended')
    setVideoButtonLabel('Replay')
    setVideoIcon(() => IterationsIcon)
  }

  // Auto-play videos
  //
  // Starts playing videos when they're in view

  interface VideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
    src: string
    poster: string
  }

  function AutoPlayVideo({src, poster, ...props}: VideoProps) {
    const videoRef = useAutoplayVideo()

    return (
      <video ref={videoRef} playsInline={true} muted={true} preload="none" poster={poster} {...props}>
        <source src={src} type="video/mp4; codecs=avc1.4d002a" />
      </video>
    )
  }

  function useAutoplayVideo() {
    const videoRef = useRef<HTMLVideoElement | null>(null)

    useEffect(() => {
      const currentVideoRef = videoRef.current

      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (currentVideoRef) currentVideoRef.play()
            } else {
              if (currentVideoRef) currentVideoRef.pause()
            }
          }
        },
        {
          threshold: 0.5, // Play once 50% of the height is...
          rootMargin: `-25% 0% -25% 0%`, // ...in the center 50% of the viewport
        },
      )

      if (currentVideoRef) {
        observer.observe(currentVideoRef)
      }

      return () => {
        if (currentVideoRef) {
          observer.unobserve(currentVideoRef)
        }
      }
    }, [])

    return videoRef
  }

  // Pricing deep link
  //
  // This is an alternative to using `href="#pricing"` on the Hero "Compare plans" button
  // It seems deep linking currently doesn't work reliably, so we're using `scrollTo()` instead

  const ctaPricingRef = useRef<HTMLButtonElement>(null)
  const sectionPricingRef = useRef<HTMLElement>(null)

  const handlePricingClick = (event: Event) => {
    event.preventDefault()

    if (sectionPricingRef.current) {
      // Set focus to the pricing section
      sectionPricingRef.current.setAttribute('tabindex', '-1')
      sectionPricingRef.current.focus({preventScroll: true})

      // Then scroll to the pricing section
      window.scrollTo({
        top: sectionPricingRef.current.offsetTop,
      })
    }
  }

  useEffect(() => {
    const currentCtaPricingRef = ctaPricingRef.current

    if (currentCtaPricingRef) {
      currentCtaPricingRef.addEventListener('click', handlePricingClick)
    }

    return () => {
      if (currentCtaPricingRef) {
        currentCtaPricingRef.removeEventListener('click', handlePricingClick)
      }
    }
  }, [])

  return (
    <>
      <ThemeProvider colorMode="dark" className="lp-Copilot">
        <section id="hero" className="lp-Section lp-Section--hero">
          <Grid>
            <Grid.Column span={12}>
              <Hero data-hpc align="center" className="lp-Hero">
                <Hero.Label size="large" leadingVisual={<CopilotIcon />}>
                  GitHub Copilot
                </Hero.Label>
                <Hero.Heading size="1" weight="bold" className="lp-Hero-heading">
                  The world’s most widely adopted AI developer tool.
                </Hero.Heading>
                <div className="lp-Hero-ctaButtons">
                  {isCopilotPlansPageEnabled ? (
                    <Hero.PrimaryAction href="/features/copilot/plans" className="Button--heroCta">
                      Get started with Copilot
                    </Hero.PrimaryAction>
                  ) : (
                    <>
                      <Hero.PrimaryAction href={copilotHeroSignupPath} className="Button--heroCta">
                        Buy now
                      </Hero.PrimaryAction>
                      <Hero.SecondaryAction
                        href=""
                        hasArrow={false}
                        className="Button--heroCta lp-Button--onColoredBg"
                        ref={ctaPricingRef}
                      >
                        Compare plans
                      </Hero.SecondaryAction>
                    </>
                  )}
                </div>
              </Hero>

              <Box className="lp-Hero-visual">
                <Box className="lp-Hero-videoContainer">
                  <video
                    autoPlay={true}
                    playsInline={true}
                    muted={true}
                    className="lp-Hero-video lp-Hero-video--landscape hide-reduced-motion"
                    width="1248"
                    height="735"
                    poster={heroVideoLgPoster}
                    ref={videoLgRef}
                    onEnded={() => handleVideoStateEnd()}
                  >
                    <source src={heroVideoLg} type="video/mp4; codecs=avc1.4d002a" />
                  </video>

                  <video
                    autoPlay={true}
                    playsInline={true}
                    muted={true}
                    className="lp-Hero-video lp-Hero-video--portrait hide-reduced-motion"
                    width="539.5"
                    height="682"
                    ref={videoSmRef}
                    onEnded={() => handleVideoStateEnd()}
                  >
                    <source src={heroVideoSm} type="video/mp4; codecs=avc1.4d002a" />
                  </video>

                  <Image
                    className="lp-Hero-videoImage hide-no-pref-motion"
                    src="/images/modules/site/copilot/hero.jpg"
                    alt="Editor with GitHub Copilot Chat"
                    width="1248"
                    loading="lazy"
                  />
                </Box>

                <Box paddingBlockStart={12} className="lp-Hero-videoPlayer">
                  <Button
                    leadingVisual={VideoIcon}
                    variant="subtle"
                    hasArrow={false}
                    className="lp-Hero-videoPlayerButton"
                    onClick={handleVideoStateChange}
                  >
                    {videoButtonLabel}
                  </Button>
                </Box>

                <Box className="lp-Hero-head hide-reduced-motion">
                  <div className="lp-Hero-headSize">
                    <div className="lp-Hero-headBlink js-copilot-head-wrapper">
                      <canvas className="js-copilot-head" data-analytics-event={copilotHydroTracking} />
                    </div>
                  </div>
                </Box>
              </Box>

              <LogoSuite hasDivider={false}>
                <LogoSuite.Heading visuallyHidden>GitHub Copilot is used by</LogoSuite.Heading>
                <LogoSuite.Logobar marquee marqueeSpeed="slow">
                  <Image src="/images/modules/site/copilot/logos/coca-cola.svg" alt="Coca Cola's logo" height={40} />
                  <Image
                    src="/images/modules/site/copilot/logos/coyote-logistics.svg"
                    alt="Coyote Logistics's logo"
                    height={40}
                  />
                  <Image src="/images/modules/site/copilot/logos/duolingo.svg" alt="Duolingo's logo" height={40} />
                  <Image src="/images/modules/site/copilot/logos/stripe.svg" alt="Stripe's logo" height={44} />
                  <Image src="/images/modules/site/copilot/logos/shopify.svg" alt="Shopify's logo" height={40} />
                  <Image
                    src="/images/modules/site/copilot/logos/mercado-libre.svg"
                    alt="Mercado Libre's logo"
                    height={40}
                  />
                  <Image src="/images/modules/site/copilot/logos/mercedes.svg" alt="Mercedes Benz's logo" height={48} />
                  <Image src="/images/modules/site/copilot/logos/fidelity.svg" alt="Fidelity's logo" height={36} />
                  <Image src="/images/modules/site/copilot/logos/dell.svg" alt="Dell's logo" height={60} />
                  <Image src="/images/modules/site/copilot/logos/lemonade.svg" alt="Lemonade's logo" height={36} />
                  <Image
                    src="/images/modules/site/copilot/logos/sas.svg"
                    alt="Scandinavian Airlines's logo"
                    height={36}
                  />
                  <Image src="/images/modules/site/copilot/logos/itau.svg" alt="Itau's logo" height={50} />
                  <Image src="/images/modules/site/copilot/logos/hover.svg" alt="Hover's logo" height={36} />
                  <Image src="/images/modules/site/copilot/logos/lyft.svg" alt="Lyft's logo" height={64} />
                </LogoSuite.Logobar>
              </LogoSuite>
            </Grid.Column>
          </Grid>

          <Box style={{height: 0}}>
            <AnchorNav hideUntilSticky>
              <AnchorNav.Link href="#enterprise">Enterprise-ready</AnchorNav.Link>
              <AnchorNav.Link href="#features">Features</AnchorNav.Link>
              <AnchorNav.Link href="#pricing">Pricing</AnchorNav.Link>
              <AnchorNav.Link href="#faq">FAQs</AnchorNav.Link>
              {isCopilotPlansPageEnabled ? (
                <AnchorNav.Action href="/features/copilot/plans">Get started</AnchorNav.Action>
              ) : (
                <AnchorNav.Action href={copilotHeroSignupPath}>Buy now</AnchorNav.Action>
              )}
            </AnchorNav>
          </Box>
          <div className="lp-Hero-cover" />
        </section>

        <section id="enterprise" className="lp-Section lp-Section--level-1">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Label size="large" className="lp-Label--section">
                  Enterprise-ready
                </SectionIntro.Label>
                <SectionIntro.Heading size="1" weight="bold">
                  The competitive advantage developers ask for by name.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <Bento className="Bento Bento--inset">
                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 7}}
                  rowSpan={{xsmall: 4, small: 3, medium: 4, large: 5, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      Proven to increase developer productivity and accelerate the pace of software development.
                    </Bento.Heading>
                    <Link
                      href="https://github.blog/2022-09-07-research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/"
                      size="large"
                      variant="default"
                    >
                      Read the research
                    </Link>
                  </Bento.Content>
                  <Bento.Visual>
                    <Image
                      src="/images/modules/site/copilot/enterprise-1.jpg"
                      alt=""
                      width="724"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 5}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 5, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    verticalAlign="end"
                    className="lp-Bento-lastChildNoMarginBottom"
                  >
                    <Bento.Heading as="h3" size="display" className="is-sansSerifAlt">
                      55%
                    </Bento.Heading>
                    <Text as="p" size="400" weight="medium" variant="muted" style={{marginBottom: '0'}}>
                      Faster coding
                    </Text>
                  </Bento.Content>
                  <Bento.Visual position="0% 0%">
                    <Image
                      src="/images/modules/site/copilot/enterprise-2.webp"
                      alt=""
                      width="492"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 5}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    verticalAlign="end"
                    className="lp-Bento-lastChildNoMarginBottom"
                  >
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Designed by leaders in AI so you can build with confidence.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual position="0% 0%">
                    <Image
                      src="/images/modules/site/copilot/enterprise-3.webp"
                      alt=""
                      width="492"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 7}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      Committed to your privacy, security, and trust.
                    </Bento.Heading>
                    <Link href="https://resources.github.com/copilot-trust-center/" size="large" variant="default">
                      Visit the Copilot Trust Center
                    </Link>
                  </Bento.Content>
                  <Bento.Visual>
                    <Image
                      src="/images/modules/site/copilot/enterprise-4.jpg"
                      alt=""
                      width="724"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={12}
                  rowSpan={{xsmall: 5, small: 5, medium: 4, large: 4, xlarge: 5}}
                  flow={{xsmall: 'row', medium: 'column'}}
                  className="Bento-item"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Duolingo empowers its engineers to be force multipliers for expertise with GitHub Copilot,
                      Codespaces.
                    </Bento.Heading>
                    <Link href="https://github.com/customer-stories/duolingo" size="large" variant="default">
                      Read customer story
                    </Link>
                  </Bento.Content>
                  <Bento.Visual padding="normal">
                    <Image
                      src="/images/modules/site/copilot/enterprise-5.jpg"
                      alt="Mobile device using Duolingo app"
                      width="570"
                      height="532"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>
              </Bento>
            </Grid.Column>
          </Grid>
        </section>

        <section
          id="productivity"
          className="lp-Section lp-Section--compact lp-Section--level-gradient lp-Section--illu"
        >
          <Grid
            className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap lp-Section--illu-content"
            style={{paddingBottom: '32px'}}
          >
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Heading size="3">
                  The industry <br className="break-whenNarrow" /> standard.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <AnimationProvider runOnce={true}>
                <Stack
                  direction={{narrow: 'vertical', regular: 'horizontal'}}
                  gap={32}
                  padding="none"
                  className="lp-Productivity"
                >
                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      37,000+
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Businesses have adopted GitHub Copilot
                    </Text>
                  </Box>

                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      1 in 3
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Fortune 500 companies use GitHub Copilot
                    </Text>
                    <Text size="200" variant="muted" style={{marginTop: '28px'}}>
                      Available for business since Dec 2022
                    </Text>
                  </Box>

                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      55%
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Developer preference for GitHub Copilot
                    </Text>
                    <Text size="200" variant="muted" style={{marginTop: '28px'}}>
                      Stack Overflow 2023 Survey
                    </Text>
                  </Box>
                </Stack>
              </AnimationProvider>
            </Grid.Column>
          </Grid>

          <Image
            src="/images/modules/site/copilot/productivity-bg-2.webp"
            alt=""
            width="600"
            height="350"
            className="lp-Productivity-bg--kite2"
            loading="lazy"
          />
          <Image
            src="/images/modules/site/copilot/productivity-bg-1.webp"
            alt=""
            width="706"
            height="338"
            className="lp-Productivity-bg--kite1"
            loading="lazy"
          />
          <Image
            src="/images/modules/site/copilot/productivity-bg-head.png"
            alt=""
            width="234"
            height="204"
            className="lp-Section--illu-img lp-Productivity-bg--mascot"
            loading="lazy"
          />
        </section>

        <section id="features" className="lp-Section lp-Section--noBottomPadding">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Label size="large" className="lp-Label--section">
                  Features
                </SectionIntro.Label>
                <SectionIntro.Heading as="h2" size="1" weight="bold">
                  The AI coding assistant <br className="break-whenWide" /> elevating developer workflows.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <RiverBreakout style={{paddingTop: '0'}}>
                <RiverBreakout.A11yHeading>Accelerate workflows</RiverBreakout.A11yHeading>
                <RiverBreakout.Visual aria-hidden="true">
                  <AutoPlayVideo
                    src="/images/modules/site/copilot/features-breakout.mp4"
                    poster="/images/modules/site/copilot/features-breakout-poster.webp"
                    width="1248"
                    height="647"
                    className="d-none d-md-block hide-reduced-motion"
                  />
                  <AutoPlayVideo
                    src="/images/modules/site/copilot/features-breakout-sm.mp4"
                    poster="/images/modules/site/copilot/features-breakout-poster-sm.webp"
                    width="350"
                    height="380"
                    className="d-block d-md-none hide-reduced-motion"
                  />
                  <Image
                    src="/images/modules/site/copilot/features-breakout.webp"
                    alt="Screenshot showing GitHub Copilot"
                    width="1248"
                    height="647"
                    style={{display: 'block'}}
                    className="hide-no-pref-motion"
                    loading="lazy"
                  />
                </RiverBreakout.Visual>
                <RiverBreakout.Content
                  trailingComponent={() => (
                    <Timeline className="lp-Timeline">
                      <Timeline.Item>
                        <em>Improve code quality and security.</em> Developers feel{' '}
                        <a
                          className="lp-Link--inline"
                          href="https://github.blog/2023-10-10-research-quantifying-github-copilots-impact-on-code-quality/"
                        >
                          more confident in their code quality
                        </a>{' '}
                        when authoring code with Copilot. And with the built-in{' '}
                        <a
                          className="lp-Link--inline"
                          href="https://github.blog/2023-02-14-github-copilot-now-has-a-better-ai-model-and-new-capabilities/#filtering-out-security-vulnerabilities-with-a-new-ai-system"
                        >
                          vulnerability prevention system
                        </a>
                        , insecure coding patterns get blocked in real time.
                      </Timeline.Item>
                      <Timeline.Item>
                        <em>Enable greater collaboration.</em> Copilot’s the newest member of your team. You can ask
                        general programming questions or very specific ones about your codebase to get answers fast,
                        learn your way around, explain a mysterious regex, or get suggestions on how to improve legacy
                        code.
                      </Timeline.Item>
                    </Timeline>
                  )}
                >
                  <Text>
                    <em>Start a conversation about your codebase.</em> Whether you’re hunting down a bug or designing a
                    new feature—when you’re stuck, ask <br className="break-whenWide" />
                    GitHub Copilot.
                  </Text>
                </RiverBreakout.Content>
              </RiverBreakout>

              <River imageTextRatio="60:40">
                <River.Visual aria-hidden="true">
                  <AutoPlayVideo
                    src="/images/modules/site/copilot/features-river-1.mp4"
                    poster="/images/modules/site/copilot/features-river-1-poster.webp"
                    width="708"
                    height="472"
                    className="d-none d-md-block hide-reduced-motion"
                  />
                  <AutoPlayVideo
                    src="/images/modules/site/copilot/features-river-1-sm.mp4"
                    poster="/images/modules/site/copilot/features-river-1-poster-sm.webp"
                    width="350"
                    height="290"
                    className="d-block d-md-none hide-reduced-motion"
                  />
                  <Image
                    src="/images/modules/site/copilot/features-river-1.webp"
                    alt="Editor with GitHub Copilot Chat"
                    width="708"
                    height="472"
                    className="hide-no-pref-motion"
                    loading="lazy"
                  />
                </River.Visual>
                <River.Content>
                  <Heading as="h3">Get AI-based suggestions in real time.</Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    GitHub Copilot suggests code completions as developers type and turns natural language prompts into
                    coding suggestions based on the project&apos;s context and style conventions.
                  </Text>
                </River.Content>
              </River>

              <River imageTextRatio="60:40">
                <River.Visual aria-hidden="true">
                  <AutoPlayVideo
                    src="/images/modules/site/copilot/features-river-2.mp4"
                    poster="/images/modules/site/copilot/features-river-2-poster.webp"
                    width="708"
                    height="472"
                    className="d-none d-md-block hide-reduced-motion"
                  />
                  <AutoPlayVideo
                    src="/images/modules/site/copilot/features-river-2-sm.mp4"
                    poster="/images/modules/site/copilot/features-river-2-poster-sm.webp"
                    width="350"
                    height="290"
                    className="d-block d-md-none hide-reduced-motion"
                  />
                  <Image
                    src="/images/modules/site/copilot/features-river-2.webp"
                    alt="GitHub Copilot showing different credential sources"
                    width="708"
                    height="472"
                    className="hide-no-pref-motion"
                    loading="lazy"
                  />
                </River.Visual>
                <River.Content>
                  <Heading as="h3">
                    <Label className="lp-Label--inRiver">Copilot Enterprise</Label>
                    Docs that feel tailored for you.
                  </Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    Spend less time searching and more time learning, by getting personalized answers that are grounded
                    in your organization’s documentation, with inline citations. Load content → Ask question → Profit.
                  </Text>
                </River.Content>
              </River>

              <River imageTextRatio="60:40" style={{paddingBottom: '0'}}>
                <River.Visual aria-hidden="true">
                  <AutoPlayVideo
                    src="/images/modules/site/copilot/features-river-3.mp4"
                    poster="/images/modules/site/copilot/features-river-3-poster.webp"
                    width="708"
                    height="472"
                    className="d-none d-md-block hide-reduced-motion"
                  />
                  <AutoPlayVideo
                    src="/images/modules/site/copilot/features-river-3-sm.mp4"
                    poster="/images/modules/site/copilot/features-river-3-poster-sm.webp"
                    width="350"
                    height="290"
                    className="d-block d-md-none hide-reduced-motion"
                  />
                  <Image
                    src="/images/modules/site/copilot/features-river-3.webp"
                    alt="GitHub Copilot adding a summary to a pull request"
                    width="708"
                    height="472"
                    className="hide-no-pref-motion"
                    loading="lazy"
                  />
                </River.Visual>
                <River.Content>
                  <Heading as="h3">
                    <Label className="lp-Label--inRiver">Copilot Enterprise</Label>
                    Pull requests that tell a story.
                  </Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    GitHub Copilot keeps track of your work, suggests descriptions, and helps reviewers reason about
                    your changes.
                  </Text>
                </River.Content>
              </River>
            </Grid.Column>

            {/* Spacer */}
            <Box paddingBlockStart={{narrow: 96, regular: 128}} aria-hidden={true} />

            <Grid.Column span={12}>
              <Bento className="Bento Bento--raised">
                <Bento.Item
                  columnSpan={12}
                  rowSpan={{xsmall: 8, small: 6, medium: 5, large: 5}}
                  visualAsBackground
                  className="lp-Features-bento-5"
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    className="lp-Features-bento-5-content"
                  >
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      <Label className="lp-Label--inBento">Copilot Enterprise</Label>
                      Prefer bespoke? Fine-tune a private copilot for the precision enterprises demand.
                    </Bento.Heading>
                    <Link href="https://github.co/copilot-custom-models" size="large" variant="default">
                      Join the waitlist
                    </Link>
                  </Bento.Content>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 7}}
                  rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} verticalAlign="start">
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Ask for assistance right in your terminal.
                    </Bento.Heading>
                    <Link
                      href="https://docs.github.com/copilot/github-copilot-in-the-cli"
                      size="large"
                      variant="default"
                    >
                      Try Copilot in the CLI
                    </Link>
                  </Bento.Content>
                  <Bento.Visual position="0% 50%">
                    <Image
                      src="/images/modules/site/copilot/features-bento-1.webp"
                      alt="Screenshot of GitHub Copilot CLI in a terminal"
                      width="724"
                      height="620"
                      className="lp-Features-bento-1-visual"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 5}}
                  rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
                  className="Bento-item"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} horizontalAlign="center">
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Keep flying with your favorite editor.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    fillMedia={false}
                    className="lp-Features-editorContainer"
                  >
                    <Button
                      as="a"
                      href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilot"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-vscode.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        VS Code
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://docs.github.com/copilot/getting-started-with-github-copilot?tool=vimneovim"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-neovim.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        Neovim
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilotvs"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-visualstudio.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        Visual Studio
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://plugins.jetbrains.com/plugin/17718-github-copilot"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-jetbrains.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        JetBrains IDEs
                      </Text>
                    </Button>
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 5, large: 5, xlarge: 5}}
                  rowSpan={{xsmall: 4, small: 4, medium: 4, xlarge: 5}}
                  className="Bento-item"
                >
                  <Bento.Content
                    horizontalAlign={{xsmall: 'center', large: 'start'}}
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    leadingVisual={<DeviceMobileIcon />}
                    className="lp-Features-mobile"
                  >
                    <Bento.Heading as="h3" size="4" weight="semibold" className="lp-Features-mobileText">
                      Coming soon to <br className="break-whenNarrow" /> GitHub Mobile.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    fillMedia={false}
                    horizontalAlign="center"
                    verticalAlign="end"
                    style={{columnGap: '24px', flexWrap: 'wrap'}}
                  >
                    <a href="https://play.google.com/store/apps/details?id=com.github.android">
                      <Image
                        src="/images/modules/site/copilot/features-bento-4-google.png"
                        alt="Google Play Store logo"
                        width="180"
                        height="53"
                        style={{display: 'block'}}
                        loading="lazy"
                      />
                    </a>
                    <a href="https://apps.apple.com/app/github/id1477376905?ls=1">
                      <Image
                        src="/images/modules/site/copilot/features-bento-4-apple.png"
                        alt="Apple App Store logo"
                        width="179"
                        height="53"
                        style={{display: 'block'}}
                        loading="lazy"
                      />
                    </a>
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 7, large: 7, xlarge: 7}}
                  rowSpan={{xsmall: 4, small: 3, medium: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Visual position="25% 0%">
                    <Image
                      src="/images/modules/site/copilot/features-bento-3.webp"
                      alt="A phone showing GitHub Copilot in GitHub Mobile"
                      width="724"
                      height="560"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>
              </Bento>
            </Grid.Column>
          </Grid>
        </section>

        <section id="pricing" className="lp-Section lp-Section--pricing" ref={sectionPricingRef}>
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Label size="large" className="lp-Label--section">
                  Pricing
                </SectionIntro.Label>
                <SectionIntro.Heading size="1" weight="bold">
                  Take flight with GitHub Copilot.
                </SectionIntro.Heading>
                <SectionIntro.Description className="lp-SectionIntro-description--wide">
                  Organizations and developers all over the world use GitHub Copilot to code faster, drive impact, and
                  focus on doing what matters most: building great software.
                </SectionIntro.Description>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <Heading as="h3" size="5" className="lp-Pricing-groupTitle">
                For organizations
              </Heading>

              <Grid className="lp-Pricing">
                <Grid.Column span={12} className="lp-Pricing-item has-BlurredBg has-GradientBorder">
                  <Stack
                    direction={{narrow: 'vertical', regular: 'horizontal'}}
                    gap="none"
                    padding="none"
                    className="lp-Pricing--business"
                  >
                    {/* Copilot */}
                    <Stack padding="none" gap={24} style={{flex: '1'}}>
                      <Box>
                        <Text size="100" weight="semibold" className="lp-Label--pricing">
                          Recommended
                        </Text>
                        <Box marginBlockStart={8} marginBlockEnd={16}>
                          <Heading as="h4" size="5">
                            Copilot Business
                          </Heading>
                        </Box>
                        <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                          Copilot in the coding environment.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                        <Text size="500" weight="normal" style={{lineHeight: 1.4}} className="is-sansSerifAlt">
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          19
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per user / month
                        </Text>
                      </Stack>

                      <hr className="lp-Pricing-divider" />

                      <UnorderedList variant="checked" className="lp-Pricing-list">
                        <UnorderedList.Item>Code completions</UnorderedList.Item>
                        <UnorderedList.Item>
                          Chat in IDE <sup>1</sup> and Mobile <sup>2</sup>
                        </UnorderedList.Item>
                        <UnorderedList.Item>
                          CLI assistance <sup>3</sup>
                        </UnorderedList.Item>
                        <UnorderedList.Item>Security vulnerability filter</UnorderedList.Item>
                        <UnorderedList.Item>Code referencing</UnorderedList.Item>
                        <UnorderedList.Item>Public code filter</UnorderedList.Item>
                        <UnorderedList.Item>IP indemnity</UnorderedList.Item>
                        <UnorderedList.Item>Enterprise-grade security, safety, and privacy</UnorderedList.Item>
                      </UnorderedList>

                      <Stack direction={{narrow: 'vertical', wide: 'horizontal'}} gap={12} padding="none">
                        <Button as="a" href={copilotForBusinessSignupPath} block variant="primary">
                          Buy now
                        </Button>
                        <Button as="a" href={copilotContactSalesPath} block className="lp-Button--onColoredBg">
                          Contact sales
                        </Button>
                      </Stack>
                    </Stack>

                    <hr className="lp-Pricing-divider lp-Pricing-divider--business" />

                    {/* Copilot Enterprise */}
                    <Stack padding="none" gap={24} style={{flex: '1'}}>
                      <Box>
                        <Text size="100" weight="semibold" className="lp-Label--pricing">
                          Available Feb 2024
                        </Text>
                        <Box marginBlockStart={8} marginBlockEnd={16}>
                          <Heading as="h4" size="5">
                            Copilot Enterprise
                          </Heading>
                        </Box>
                        <Text as="p" size="200" weight="normal" variant="muted" className="lp-Pricing-description--org">
                          Copilot personalized to your organization throughout the software development lifecycle.
                          Requires GitHub Enterprise Cloud.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none" className="lp-Pricing-price">
                        <Text size="500" weight="normal" className="is-sansSerifAlt" style={{lineHeight: 1.4}}>
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          39
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per user / month
                        </Text>
                      </Stack>

                      <hr className="lp-Pricing-divider" />

                      <Box style={{flex: '1'}}>
                        <Box className="lp-Pricing-plus">
                          <ArrowLeftIcon size={16} />
                          <Text size="200" weight="normal" className="lp-Pricing-plusText">
                            Everything in Copilot, plus:
                          </Text>
                        </Box>

                        <UnorderedList variant="checked" className="lp-Pricing-list">
                          <UnorderedList.Item>Chat personalized to your codebase</UnorderedList.Item>
                          <UnorderedList.Item>Documentation search and summaries</UnorderedList.Item>
                          <UnorderedList.Item>Pull request summaries</UnorderedList.Item>
                          <UnorderedList.Item>Code review skills</UnorderedList.Item>
                          <UnorderedList.Item>
                            Fine-tuned models <sup>4</sup>
                          </UnorderedList.Item>
                        </UnorderedList>
                      </Box>
                      <Button as="a" href="https://github.com/features/preview/copilot-enterprise" variant="primary">
                        Join waitlist
                      </Button>
                    </Stack>
                  </Stack>
                </Grid.Column>
              </Grid>
            </Grid.Column>

            <Grid.Column span={12}>
              <Heading as="h3" size="5" className="lp-Pricing-groupTitle lp-Pricing-groupTitle--individuals">
                For individuals
              </Heading>

              <Grid className="lp-Pricing">
                <Grid.Column
                  span={12}
                  className="lp-Pricing-item has-BlurredBg has-GradientBorder"
                  style={{gap: '16px', backgroundColor: 'rgba(22, 27, 34, 0.85)'}}
                >
                  <Heading as="h4" size="5">
                    Copilot Individual
                  </Heading>

                  <Stack
                    direction={{narrow: 'vertical', regular: 'horizontal'}}
                    gap={24}
                    padding="none"
                    className="lp-Pricing--multiColumn"
                  >
                    <Box style={{flex: '1'}}>
                      <Box marginBlockEnd={32}>
                        <Text as="p" size="200" weight="normal" variant="muted">
                          Code completions, Chat, and more for indie developers and freelancers.
                        </Text>
                      </Box>

                      <Stack direction="horizontal" gap={12} padding="none">
                        <Text size="500" weight="normal" className="is-sansSerifAlt" style={{lineHeight: 1.4}}>
                          $
                        </Text>
                        <Text weight="normal" className="lp-Pricing-nr is-sansSerifAlt">
                          10
                        </Text>
                        <Text size="100" weight="normal" variant="muted" style={{alignSelf: 'center'}}>
                          per month / <br className="break-whenWide" />
                          $100 per year
                        </Text>
                      </Stack>
                    </Box>

                    <Box style={{flex: '1'}}>
                      <Button as="a" href={copilotSignupPath} block variant="primary">
                        Start a free trial
                      </Button>
                      <Text
                        as="p"
                        size="100"
                        weight="normal"
                        variant="muted"
                        style={{marginTop: 'var(--base-size-32)'}}
                      >
                        Free for verified students, teachers, and maintainers of popular open source projects.
                      </Text>
                    </Box>
                  </Stack>
                </Grid.Column>
              </Grid>
            </Grid.Column>
          </Grid>

          {/* Spacer */}
          <Box paddingBlockStart={32} aria-hidden={true} />

          <div className="lp-Pricing-bgCover">
            <Image
              className="lp-Pricing-bgCoverImage"
              src="/images/modules/site/copilot/pricing-cover.svg"
              loading="lazy"
              alt=""
            />
          </div>
        </section>

        <section id="resources" className="lp-Section lp-Section--compact">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Heading size="3">Get the most out of GitHub Copilot.</SectionIntro.Heading>
              </SectionIntro>

              <Stack
                direction={{narrow: 'vertical', regular: 'horizontal', wide: 'horizontal'}}
                gap="normal"
                padding="none"
              >
                <div className="lp-Resources-card">
                  <Card ctaText="Explore GitHub Expert Services" href="https://github.com/services/">
                    <Card.Icon icon={<PeopleIcon />} color="purple" hasBackground />
                    <Card.Heading>Hands-on consulting, guided workshops, and training.</Card.Heading>
                    <Card.Description>
                      Insights, best practices, and knowledge to help you adopt GitHub quickly and efficiently.
                    </Card.Description>
                  </Card>
                </div>

                <div className="lp-Resources-card">
                  <Card ctaText="Read customer stories" href="https://github.com/customer-stories">
                    <Card.Icon icon={<BookIcon />} color="purple" hasBackground />
                    <Card.Heading>Meet the companies who build with GitHub.</Card.Heading>
                    <Card.Description>
                      Leading organizations choose GitHub to plan, build, secure and ship software.
                    </Card.Description>
                  </Card>
                </div>

                <div className="lp-Resources-card">
                  <Card ctaText="Read blog" href="https://github.blog/">
                    <Card.Icon icon={<BookIcon />} color="purple" hasBackground />
                    <Card.Heading>Keep up with the latest on GitHub and trends in AI.</Card.Heading>
                    <Card.Description>
                      Check out the GitHub blog for tips, technical guides, best practices, and more.
                    </Card.Description>
                  </Card>
                </div>
              </Stack>
            </Grid.Column>
          </Grid>
        </section>

        <section id="faq" className="lp-Section lp-Section--level-1">
          <Grid>
            <Grid.Column span={12} className="lp-FAQs">
              <ContentfulFaqGroup component={page.fields.template.fields.faqGroup} />
            </Grid.Column>
          </Grid>
        </section>

        <section id="footnotes" className="lp-Section lp-Section--level-1" style={{paddingTop: '0'}}>
          <Grid className="lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <OrderedList>
                <OrderedList.Item className="lp-Footnotes-item">
                  Chat in Visual Studio Code and Visual Studio now generally available. Chat in JetBrains IDEs currently
                  in preview and generally available in 2024. Instructions to enable the preview can be found{' '}
                  <a
                    className="lp-Link--inline"
                    href="https://docs.github.com/copilot/github-copilot-chat/using-github-copilot-chat-in-your-ide?tool=jetbrains"
                  >
                    here
                  </a>
                  .
                </OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">Chat in GitHub Mobile is coming soon.</OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">
                  Currently in preview. Instructions to enable Copilot in the CLI in preview can be found{' '}
                  <a className="lp-Link--inline" href="https://docs.github.com/copilot/github-copilot-in-the-cli">
                    here
                  </a>
                  .
                </OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">
                  Currently in preview. Copilot Enterprise grants you access to customization for fine-tuned models
                  (available in 2024).
                </OrderedList.Item>
              </OrderedList>
            </Grid.Column>
          </Grid>
        </section>
      </ThemeProvider>
    </>
  )
}

try{ ContentfulIndex.displayName ||= 'ContentfulIndex' } catch {}
try{ AutoPlayVideo.displayName ||= 'AutoPlayVideo' } catch {}